import React, { Suspense } from 'react';

import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { isEmpty } from '@/shared/utility';
import { useCashierShow } from '@/features/cashier/useCashier';
import CloseButton from '@/components/UI/Buttons/CloseButton';
import { selectUser } from '@/store/selectors/userSelectors';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';

const MenuDrawerUser = React.lazy(() => import('./MenuDrawerUser'));
const MenuDrawerNavThailand = React.lazy(() => import('./MenuDrawerNavThailand'));
const MenuDrawerNav = React.lazy(() => import('./MenuDrawerNav'));

const StyledMenuDrawer = styled.div`
  max-width: 400px !important;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  nav {
    padding: 64px 32px 0;

    @media (min-height: 600px) {
      padding: 100px 32px 0;
    }
  }

  ul {
    padding: 0;
    margin: 40px 0 0 0;

    li {
      display: block;
      list-style: none;
      margin: 0;
      padding: 0;

      &.margin-top {
        margin-top: 32px;
      }

      a,
      button {
        display: inline-flex;
        align-items: center;
        padding: 8px 0;
        border: none;
        background: none;
        text-decoration: none;
        color: ${props => props.theme.colors.blue3};
        transition: all 0.2s ease;
        font-size: 18px;
        font-weight: bold;
        position: relative;
        text-align: left;
        cursor: pointer;

        &.small {
          font-size: 15px;
          opacity: 0.6;
          padding: 4px 0;
        }

        &.active {
          color: ${props => props.theme.colors.blue1};
        }

        svg {
          height: 22px;
          width: 22px;
          margin-right: 16px;
          color: black;
          opacity: 0.4;
        }

        .notification-badge {
          left: 100%;
          top: 3px;
        }
      }
    }
  }

  .button-wrapper {
    display: grid;
    padding: 32px;

    @media (min-height: 600px) {
      padding: 32px 32px 64px;
    }

    .create-account {
      width: 100%;
      border-radius: 50vh;
      padding: 1em 1.5em;
      font-size: 18px;
      margin-top: 32px;
      color: #fff;
      background: ${props => props.theme.colors.blue1};
      border: none;
      font-weight: bold;
      transition: all 0.15s ease;
      -webkit-appearance: none;
      cursor: pointer;
      box-shadow:
        0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -2px rgba(0, 0, 0, 0.05);

      &:hover {
        background: ${props => props.theme.colors.blue1Hover};
      }
    }
  }
`;

function MenuDrawer({ closeDrawer }: { closeDrawer: () => void }): JSX.Element {
  const user = useSelector(selectUser);
  const locale = useSelector(selectLocale);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const showCashier = useCashierShow();

  const handleOpenCashier = () => {
    // as cashier is on a separate drawer we need to close the menu drawer before opening the cashier drawer
    closeDrawer();
    showCashier();
  };

  return (
    <StyledMenuDrawer className="drawer" role="presentation" aria-label="Menu">
      <div className="flex flex-col justify-between h-full">
        <CloseButton left onClick={closeDrawer} />
        <div className="overflow-y-auto">
          <Suspense fallback={<div />}>
            {locale !== 'th' && <MenuDrawerNav closeDrawer={closeDrawer} />}
            {locale === 'th' && <MenuDrawerNavThailand closeDrawer={closeDrawer} />}
          </Suspense>
        </div>
        {isAuthenticated && user && !isEmpty(user) && (
          <Suspense fallback={<div />}>
            <MenuDrawerUser user={user} clickHandler={handleOpenCashier} />
          </Suspense>
        )}
      </div>
    </StyledMenuDrawer>
  );
}

export default MenuDrawer;
